import { memo, useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { VIDEOS } from './constants';
import MemoryGame from './games/MemoryGame';
import SnakeGame from './games/SnakeGame';
import ClickerGame from './games/ClickerGame';
import ColorMatchGame from './games/ColorMatchGame';
import MusicPlayer from './games/MusicPlayer';

const LoadingView = memo(({ progress, loadingMsg }) => {
  const isDark = typeof window !== 'undefined' && localStorage.getItem('gh_marketing_theme') === 'dark';
  const [funTab, setFunTab] = useState('videos');
  const [videoIdx, setVideoIdx] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [activeGame, setActiveGame] = useState(null);

  const BUILD_STEPS = [
    { label: 'Choosing concept', threshold: 5 },
    { label: 'Designing typography', threshold: 10 },
    { label: 'Crafting hero section', threshold: 18 },
    { label: 'Writing your copy', threshold: 30 },
    { label: 'Building gallery', threshold: 42 },
    { label: 'Creating custom graphics', threshold: 55 },
    { label: 'Adding animations', threshold: 68 },
    { label: 'Polishing every detail', threshold: 80 },
  ];

  const currentStepIdx = BUILD_STEPS.findIndex(s => progress < s.threshold);
  const activeStepIdx = currentStepIdx === -1 ? BUILD_STEPS.length - 1 : Math.max(0, currentStepIdx - 1);

  return (
    <div className="animate-fadeIn flex flex-col h-full" style={{ position: 'relative' }}>
      {/* Build Steps Visualization */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontFamily: 'var(--sans)', fontSize: '16px', color: isDark ? '#f2efe9' : '#2a2725' }}>Iris is crafting your vision...</span>
          <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#C9A84C', fontWeight: 700 }}>{Math.round(progress)}%</span>
        </div>
        <div style={{ width: '100%', height: '3px', background: isDark ? 'rgba(255,255,255,0.08)' : '#e8e3dc', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: '#C9A84C', transition: 'width .5s linear' }} />
        </div>
        {/* Step-by-step build progress */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
          {BUILD_STEPS.map((step, i) => {
            const done = i < activeStepIdx;
            const active = i === activeStepIdx;
            return (
              <div key={step.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', transition: 'all .3s' }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
                  background: done ? '#C9A84C' : active ? '#C9A84C' : isDark ? 'rgba(255,255,255,0.15)' : '#e8e3dc',
                  boxShadow: active ? '0 0 8px rgba(196,162,101,0.5)' : 'none',
                  animation: active ? 'pulse 1.5s ease-in-out infinite' : 'none',
                }} />
                <span style={{
                  fontFamily: 'var(--sans)', fontSize: '9px', letterSpacing: '.04em',
                  color: done ? '#C9A84C' : active ? (isDark ? '#f2efe9' : '#2a2725') : '#8c8782',
                  fontWeight: active ? 600 : 400,
                  textDecoration: done ? 'line-through' : 'none',
                  opacity: done ? 0.5 : 1,
                }}>
                  {step.label}{active ? '...' : ''}
                </span>
              </div>
            );
          })}
        </div>
        <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', fontStyle: 'italic', color: '#8c8782', marginTop: '12px' }} key={loadingMsg}>{loadingMsg}</p>
      </div>

      <p style={{ fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.2em', textTransform: 'uppercase', color: '#C9A84C', marginBottom: '12px' }}>
        While you wait...
      </p>

      {/* Entertainment Tabs */}
      <div className="flex-1 flex flex-col rounded-xl border overflow-hidden" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e8e3dc', background: isDark ? 'rgba(255,255,255,0.03)' : '#f5f2ee' }}>
        <div className="flex border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#e8e3dc' }}>
          {[
            { key: 'videos', label: 'Videos' },
            { key: 'games', label: 'Games' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setFunTab(t.key); setActiveGame(null); }}
              className="flex-1 py-2 text-xs font-sans font-medium"
              style={{
                color: funTab === t.key ? '#C9A84C' : '#8c8782',
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: `2px solid ${funTab === t.key ? '#C9A84C' : 'transparent'}`,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {/* Videos */}
          {funTab === 'videos' && !videoPlaying && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #C9A84C, #c46a4e)', cursor: 'pointer' }} onClick={() => { setVideoPlaying(true); setVideoIdx(0); }}>
                <span style={{ color: '#fff', fontSize: '24px', marginLeft: '3px' }}>&#9654;</span>
              </div>
              <p className="font-serif text-lg font-bold mb-1" style={{ color: isDark ? '#f2efe9' : '#2a2725' }}>
                Play a Surprise Video
              </p>
              <p className="font-sans text-xs" style={{ color: '#8c8782' }}>
                Trust us. You need this right now.
              </p>
            </div>
          )}
          {funTab === 'videos' && videoPlaying && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-sans font-medium" style={{ color: isDark ? '#f2efe9' : '#2a2725' }}>
                  {VIDEOS[videoIdx].title} <span style={{ color: '#8c8782' }}>({VIDEOS[videoIdx].year})</span>
                </span>
                <button
                  onClick={() => { let n; do { n = Math.floor(Math.random() * VIDEOS.length); } while (n === videoIdx); setVideoIdx(n); }}
                  className="text-xs font-sans font-medium px-3 py-1 rounded-full flex items-center gap-1"
                  style={{ color: '#C9A84C', background: 'none', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E8E2D8', cursor: 'pointer' }}
                >
                  Shuffle
                </button>
              </div>
              <div className="rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <iframe
                  key={videoIdx}
                  src={`https://www.youtube-nocookie.com/embed/${VIDEOS[videoIdx].id}?autoplay=1&mute=0&controls=1&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&fs=0&playsinline=1`}
                  title={VIDEOS[videoIdx].title}
                  className="w-full h-full border-0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Games */}
          {funTab === 'games' && !activeGame && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[
                { key: 'memory', label: 'Memory Match', desc: 'Find pairs' },
                { key: 'clicker', label: 'Quick Tap', desc: 'Beat the clock' },
                { key: 'color', label: 'Color Match', desc: 'Test your eye' },
                { key: 'snake', label: 'Gold Trail', desc: 'Arrow keys' },
              ].map(g => (
                <button
                  key={g.key}
                  onClick={() => setActiveGame(g.key)}
                  style={{ padding: '16px', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e8e3dc', background: isDark ? 'rgba(255,255,255,0.03)' : '#faf8f5', cursor: 'pointer', textAlign: 'center', transition: 'all .3s', borderRadius: '8px' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(196,162,101,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : '#e8e3dc'; }}
                >
                  <span style={{ fontFamily: 'var(--sans)', fontSize: '15px', color: isDark ? '#f2efe9' : '#2a2725', display: 'block', marginBottom: '2px', fontWeight: 500 }}>{g.label}</span>
                  <span style={{ fontFamily: 'var(--sans)', fontSize: '8px', color: '#8c8782', letterSpacing: '.1em', textTransform: 'uppercase' }}>{g.desc}</span>
                </button>
              ))}
            </div>
          )}
          {funTab === 'games' && activeGame === 'memory' && (
            <div>
              <button onClick={() => setActiveGame(null)} className="text-xs font-sans mb-2 flex items-center gap-1" style={{ color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer' }}>
                <ArrowLeft size={10} /> Back to games
              </button>
              <MemoryGame />
            </div>
          )}
          {funTab === 'games' && activeGame === 'snake' && (
            <div>
              <button onClick={() => setActiveGame(null)} className="text-xs font-sans mb-2 flex items-center gap-1" style={{ color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer' }}>
                <ArrowLeft size={10} /> Back to games
              </button>
              <SnakeGame />
            </div>
          )}
          {funTab === 'games' && activeGame === 'clicker' && (
            <div>
              <button onClick={() => setActiveGame(null)} className="text-xs font-sans mb-2 flex items-center gap-1" style={{ color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer' }}>
                <ArrowLeft size={10} /> Back to games
              </button>
              <ClickerGame />
            </div>
          )}
          {funTab === 'games' && activeGame === 'color' && (
            <div>
              <button onClick={() => setActiveGame(null)} className="text-xs font-sans mb-2 flex items-center gap-1" style={{ color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer' }}>
                <ArrowLeft size={10} /> Back to games
              </button>
              <ColorMatchGame />
            </div>
          )}
        </div>
      </div>

      {/* Music Player */}
      <MusicPlayer />
    </div>
  );
});

LoadingView.displayName = 'LoadingView';
export default LoadingView;
